import { useEffect } from "react"

import { useKeyboardShortcutsController } from "@/contexts/KeyboardShortcutsContext"

export const ESCAPE_SHORTCUT_PRIORITY = {
  PAGE_FALLBACK: 10,
  RESULT_SURFACE: 20,
  ACTIVE_ITEM_CLEAR: 25,
  SELECTION_CLEAR: 30,
  PAGE_PANEL: 150,
  DETAIL_PANEL: 180,
  DIALOG: 220,
  EXPANDED_DIALOG: 250,
  COMMAND_PALETTE: 300,
} as const

interface UseEscapeShortcutOptions {
  allowInInputs?: boolean
  handler: () => void
  enabled?: boolean
  description?: string
  priority?: number
}

/**
 * Convenient hook specifically for ESC key handling
 * Most common use case for shortcuts in modal/page navigation
 */
export const useEscapeShortcut = ({
  allowInInputs = false,
  handler,
  enabled = true,
  description = "Close or go back",
  priority = 0,
}: UseEscapeShortcutOptions) => {
  const { registerEscapeAction, unregisterEscapeAction } =
    useKeyboardShortcutsController()

  useEffect(() => {
    if (!enabled) {
      return
    }

    const id = registerEscapeAction({
      action: handler,
      allowInInputs,
      label: description,
      priority,
    })

    return () => {
      unregisterEscapeAction(id)
    }
  }, [
    allowInInputs,
    description,
    enabled,
    handler,
    priority,
    registerEscapeAction,
    unregisterEscapeAction,
  ])
}
